import { getSupabaseClient } from './supabase';
import { trackEvent } from './analytics/posthog';
import type {
  Team,
  TeamMembership,
  CoachNote,
  TrainingPlan,
  ExerciseVideoFeedback,
  UserProfile
} from '@shared/schema';

/**
 * Make an authenticated API request with the user's JWT token
 */
async function authenticatedRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const supabase = await getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    try { trackEvent('auth_session_expired', { page_source: window.location.pathname }); } catch {}
    throw new Error('Not authenticated');
  }

  // If token is expired or about to expire (within 30s), refresh it
  let accessToken = session.access_token;
  const expiresAt = session.expires_at; // Unix timestamp in seconds
  if (expiresAt && expiresAt < Math.floor(Date.now() / 1000) + 30) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    if (refreshData.session?.access_token) {
      accessToken = refreshData.session.access_token;
    } else {
      try { trackEvent('auth_session_expired', { page_source: window.location.pathname, reason: 'refresh_failed' }); } catch {}
      throw new Error('Not authenticated');
    }
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${accessToken}`,
  };

  if (data) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let errorMessage = res.statusText;
    try {
      const errorBody = await res.text();
      if (errorBody) {
        try {
          const errorJson = JSON.parse(errorBody);
          errorMessage = errorJson.message || errorBody;
        } catch {
          errorMessage = errorBody;
        }
      }
    } catch {
      errorMessage = res.statusText;
    }
    throw new Error(`${res.status}: ${errorMessage}`);
  }

  return res;
}

// ============================================================
// Types
// ============================================================

export interface TeamMemberWithUser extends TeamMembership {
  user: {
    id: number;
    name: string | null;
    email: string;
    profilePicture: string | null;
  };
}

export interface DashboardStats {
  hasTeam: boolean;
  teamId?: number;
  teamName?: string;
  inviteCode?: string;
  totalMembers: number;
  creditsRemaining: number;
  creditsUsedThisMonth: number;
  pendingVideos?: number; // Optional - no longer displayed in UI
}

export interface PlayerProfileResponse {
  user: {
    id: number;
    name: string | null;
    email: string;
    profilePicture: string | null;
  };
  profile: UserProfile | null;
  stats: UserProfile | null;  // Alias for profile for easier access
  membership: TeamMembership;
}

// ============================================================
// Coach API
// ============================================================

export const coachApi = {
  // Team Management
  createTeam: async (name: string): Promise<Team> => {
    const res = await authenticatedRequest('POST', '/api/coach/teams', { name });
    return res.json();
  },

  getTeams: async (): Promise<Team[]> => {
    const res = await authenticatedRequest('GET', '/api/coach/teams');
    return res.json();
  },

  getTeam: async (teamId: number): Promise<Team> => {
    const res = await authenticatedRequest('GET', `/api/coach/teams/${teamId}`);
    return res.json();
  },

  updateTeam: async (teamId: number, updates: { name?: string; maxMembers?: number }): Promise<Team> => {
    const res = await authenticatedRequest('PATCH', `/api/coach/teams/${teamId}`, updates);
    return res.json();
  },

  regenerateInviteCode: async (teamId: number): Promise<{ inviteCode: string }> => {
    const res = await authenticatedRequest('POST', `/api/coach/teams/${teamId}/regenerate-invite`);
    return res.json();
  },

  // Members
  getTeamMembers: async (teamId: number): Promise<TeamMemberWithUser[]> => {
    const res = await authenticatedRequest('GET', `/api/coach/teams/${teamId}/members`);
    return res.json();
  },

  removeMember: async (teamId: number, memberId: number): Promise<void> => {
    await authenticatedRequest('DELETE', `/api/coach/teams/${teamId}/members/${memberId}`);
  },

  // Player Data
  getPlayerProfile: async (playerId: number): Promise<PlayerProfileResponse> => {
    const res = await authenticatedRequest('GET', `/api/coach/players/${playerId}`);
    return res.json();
  },

  getPlayerPlans: async (playerId: number): Promise<TrainingPlan[]> => {
    const res = await authenticatedRequest('GET', `/api/coach/players/${playerId}/plans`);
    return res.json();
  },

  getPlayerVideos: async (playerId: number): Promise<ExerciseVideoFeedback[]> => {
    const res = await authenticatedRequest('GET', `/api/coach/players/${playerId}/videos`);
    return res.json();
  },

  getPlayerNotes: async (playerId: number): Promise<CoachNote[]> => {
    const res = await authenticatedRequest('GET', `/api/coach/players/${playerId}/notes`);
    return res.json();
  },

  // Notes
  createNote: async (data: {
    athleteUserId: number;
    targetType: 'video' | 'plan';
    targetId: number;
    content: string;
  }): Promise<CoachNote> => {
    const res = await authenticatedRequest('POST', '/api/coach/notes', data);
    return res.json();
  },

  updateNote: async (noteId: number, content: string): Promise<CoachNote> => {
    const res = await authenticatedRequest('PATCH', `/api/coach/notes/${noteId}`, { content });
    return res.json();
  },

  deleteNote: async (noteId: number): Promise<void> => {
    await authenticatedRequest('DELETE', `/api/coach/notes/${noteId}`);
  },

  // Dashboard
  getDashboardStats: async (): Promise<DashboardStats> => {
    const res = await authenticatedRequest('GET', '/api/coach/dashboard/stats');
    return res.json();
  },
};
